import React, { useEffect, useState } from 'react';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import { supabase } from '../../lib/supabase';
import { useModal } from '../../components/ui/ModalProvider';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { useRequestGuard } from '../../hooks/useRequestGuard';

const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;


export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const { alert, confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startAccountsRequest = useRequestGuard();


const getAccessToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
};


  const handleDisconnect = async (accountId) => {
  if (disconnectingId) return; // prevent double-clicks / overlapping requests

  const ok = await confirm(
    "Are you sure you want to disconnect this account? All scheduled and automated publishing for this account will stop."
  );
  if (!ok) return;

  const accessToken = await getAccessToken();
  if (!accessToken) return;

  setDisconnectingId(accountId);

  try {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/pinterest-accounts`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: anonKey,
        },
        body: JSON.stringify({ accountId }),
      }
    );

    const json = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(json.error || `Request failed with status ${res.status}`);
    }

    // Remove from UI immediately — request already succeeded server-side
    setAccounts((prev) => prev.filter((a) => a.id !== accountId));
    await alert("Account disconnected successfully.", { tone: "success" });
  } catch (err) {
    console.error("Failed to disconnect Pinterest account:", err);
    await alert(`Failed to disconnect account: ${err.message || "Please try again."}`);
  } finally {
    setDisconnectingId(null);
  }
};



  // ✅ read success flag
  const success = new URLSearchParams(window.location.search).get("success");

  // =========================
  // LOAD AUTHENTICATED USER
  // =========================
  useEffect(() => {
    const loadUser = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error || !data?.user) {
        setUser(null);
      } else {
        setUser(data.user);
      }
    };

    loadUser();
  }, []);

  // =========================
  // LOAD / RELOAD ACCOUNTS
  // =========================
  // Clear the previous profile's accounts immediately on switch so stale
  // content can't flash while the new profile's data is loading.
  useEffect(() => {
    setAccounts([]);
  }, [activeProfile?.id]);

  useEffect(() => {
    if (!user) return;

    const isCurrent = startAccountsRequest();

    const loadAccounts = async () => {
      setLoading(true);

      const accessToken = await getAccessToken();
      if (!accessToken) {
        if (isCurrent()) setLoading(false);
        return;
      }

      try {
      const accountsUrl = activeProfile?.id
        ? `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/pinterest-accounts?profileId=${encodeURIComponent(activeProfile.id)}`
        : `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/pinterest-accounts`;
      const res = await fetch(
  accountsUrl,
  {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
  }
);


        const json = await res.json();
        if (!isCurrent()) return;
        setAccounts(json.accounts || []);
      } catch (err) {
        if (!isCurrent()) return;
        console.error(err);
        setAccounts([]);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    loadAccounts();

    // 🧹 clean ?success=true after reload
    if (success === "true") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [user, success, activeProfile?.id, startAccountsRequest]);

  // =========================
  // START PINTEREST OAUTH
  // =========================
  const handleConnectAccount = async () => {
    if (!user) {
      alert('User not authenticated');
      return;
    }

    const accessToken = await getAccessToken();
    if (!accessToken) {
      alert('Session expired. Please sign in again.');
      return;
    }

    try {
      const authUrl = activeProfile?.id
        ? `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/pinterest-auth?profileId=${encodeURIComponent(activeProfile.id)}`
        : `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/pinterest-auth`;
      const res = await fetch(authUrl, {
        headers: { Authorization: `Bearer ${accessToken}`, apikey: anonKey },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.url) {
        throw new Error(json.error || 'Failed to start Pinterest connection.');
      }
      window.location.href = json.url;
    } catch (err) {
      console.error('[Accounts] Failed to start Pinterest OAuth:', err);
      alert(`Failed to connect Pinterest: ${err.message || 'Please try again.'}`);
    }
  };

  if (loading) {
    return <div className="p-8">Loading…</div>;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />

        <main className="main-content">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
              Connected Pinterest Accounts
            </h1>

            {accounts.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10">
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
                    <span className="text-red-600 text-2xl font-bold">P</span>
                  </div>

                  <h2 className="text-lg font-semibold text-gray-900">
                    No Pinterest Accounts Connected
                  </h2>

                  <p className="text-sm text-gray-500 mt-2 max-w-md">
                    Connect your Pinterest account to manage boards and pins in one place.
                  </p>

                  <button
                    onClick={handleConnectAccount}
                    className="mt-6 px-6 py-2 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition"
                  >
                    Connect Account
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {accounts.map((acc) => (
                  <div
                    key={acc.id}
                    className="bg-white border border-gray-200 rounded-xl shadow-sm p-6"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center font-bold text-red-600">
                        P
                      </div>

                   <div className="flex-1">
  <p className="font-semibold text-gray-900">
    {acc.username || 'Pinterest Account'}
  </p>
  <p className="text-xs text-gray-500">Connected</p>
</div>

<button
  onClick={() => handleDisconnect(acc.id)}
  disabled={disconnectingId === acc.id}
  className="ml-auto text-sm text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
>
  {disconnectingId === acc.id ? "Disconnecting…" : "Disconnect"}
</button>

                    </div>
                  </div>
                ))}

                <div
                  onClick={handleConnectAccount}
                  className="border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-indigo-400 transition"
                >
                  <div className="text-center p-6">
                    <div className="text-indigo-600 text-2xl font-bold">+</div>
                    <p className="text-sm text-gray-600 mt-2">
                      Add another account
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
